<template>
  <div class="cc-card">
    <div class="cc-head">
      <h3 class="cc-title">Financing partner</h3>
      <span v-if="conn.connected" class="cc-badge cc-badge-on">Connected</span>
      <span v-else class="cc-badge">Not connected</span>
    </div>

    <p class="cc-help">
      Connect your practice's Cherry account so a financing option can show Cherry's real
      payment plans for each patient's amount, instead of the estimated terms typed in
      below. Only the dollar amount is sent to Cherry to price a plan — nothing about the
      patient.
    </p>

    <div v-if="loading" class="cc-loading">
      <i class="fa fa-circle-o-notch fa-spin"></i> Checking your Cherry connection…
    </div>

    <!-- ── Not connected: paste a key ─────────────────────────────────── -->
    <template v-else-if="!conn.connected">
      <div class="cc-field">
        <label>Cherry Business API Key</label>
        <input
          v-model="keyInput"
          class="cc-input"
          type="password"
          autocomplete="off"
          spellcheck="false"
          placeholder="Paste the key from Cherry"
          :disabled="busy"
          @keyup.enter="onConnect"
        />
        <p class="cc-hint">
          In Cherry, go to <strong>practice.withcherry.com → Settings → Custom Integrations</strong>
          and generate a Business API Key. It identifies your practice to Cherry. We check it with
          Cherry before saving, and it's stored encrypted — it is never shown again.
        </p>
      </div>

      <p v-if="error" class="cc-error"><i class="fa fa-exclamation-circle"></i> {{ error }}</p>

      <button class="cc-btn-primary" :disabled="busy || !keyInput.trim()" @click="onConnect">
        <i v-if="busy" class="fa fa-circle-o-notch fa-spin"></i>
        Connect Cherry
      </button>
    </template>

    <!-- ── Connected ──────────────────────────────────────────────────── -->
    <template v-else>
      <dl class="cc-summary">
        <div class="cc-summary-row">
          <dt>Cherry account</dt>
          <dd>{{ conn.businessName || '—' }}</dd>
        </div>
        <div class="cc-summary-row">
          <dt>API key</dt>
          <dd class="cc-mono">•••• {{ conn.keyLast4 || '????' }}</dd>
        </div>
        <div class="cc-summary-row">
          <dt>Last verified</dt>
          <dd>{{ validatedAtDisplay }}</dd>
        </div>
      </dl>

      <!-- Practice selection. A business can have several locations, and the choice
           decides which one Cherry attributes payments to — so it is required before
           anything can actually be used, and we say so rather than failing later. -->
      <div class="cc-field">
        <label>Cherry practice location</label>
        <select
          class="cc-input"
          :value="conn.practiceId || ''"
          :disabled="busy"
          @change="onSelectPractice($event.target.value)"
        >
          <option value="" disabled>— Choose a location —</option>
          <option v-for="p in conn.practices" :key="p.id" :value="p.id">
            {{ p.name }}<template v-if="p.status && p.status !== 'ACTIVE' && p.status !== 'SUCCESS'"> ({{ p.status }})</template>
          </option>
        </select>
        <p v-if="!conn.practiceId" class="cc-hint cc-hint-warn">
          Pick the location this Teamio site represents. Cherry attributes payments to it,
          so financing stays unavailable until one is chosen.
        </p>
        <p v-else class="cc-hint">
          Financing will be attributed to <strong>{{ conn.practiceName }}</strong> in Cherry.
        </p>
      </div>

      <p v-if="error" class="cc-error"><i class="fa fa-exclamation-circle"></i> {{ error }}</p>

      <div class="cc-actions">
        <button class="cc-btn" :disabled="busy" @click="showReplace = !showReplace">
          {{ showReplace ? 'Cancel' : 'Replace key' }}
        </button>
        <button class="cc-btn cc-btn-danger" :disabled="busy" @click="onDisconnect">
          Disconnect
        </button>
      </div>

      <div v-if="showReplace" class="cc-field cc-replace">
        <label>New Business API Key</label>
        <input
          v-model="keyInput"
          class="cc-input"
          type="password"
          autocomplete="off"
          spellcheck="false"
          placeholder="Paste the new key"
          :disabled="busy"
          @keyup.enter="onConnect"
        />
        <p class="cc-hint">
          Rotating a key in Cherry? Paste the new one here. Your chosen location is kept
          as long as Cherry still lists it.
        </p>
        <button class="cc-btn-primary" :disabled="busy || !keyInput.trim()" @click="onConnect">
          <i v-if="busy" class="fa fa-circle-o-notch fa-spin"></i>
          Verify and replace
        </button>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Cherry connection panel — lives inside Tx Presenter Settings → Payment Options,
 * directly above the options it powers, because "which lender backs this option" is
 * only answerable once an account is attached.
 *
 * The key is write-only from the browser's point of view: it is posted once, validated
 * with Cherry server-side, and stored encrypted. Nothing ever sends it back — the most
 * we display is the last four characters, which is enough for an operator to tell which
 * key is installed and useless to anyone else.
 *
 * Rendering of this card is gated on the `cherryEnabled` getter by the parent. That is a
 * courtesy, not the enforcement: cherry-service default-denies every one of these
 * endpoints server-side, so a site that isn't entitled gets a 403 regardless.
 */
import { mapActions } from 'vuex';
import mixin from '@/mixin.js';

const emptyConn = () => ({
  connected: false,
  keyLast4: null,
  businessName: '',
  practiceId: null,
  practiceName: null,
  practices: [],
  validatedAt: null,
});

export default {
  name: 'CherryConnectCard',
  mixins: [mixin],
  props: {
    // `siteId`, not `siteID` — a capitalised trailing acronym kebab-cases to `site-i-d`
    // in the parent template, which reads like a typo and invites one.
    siteId: { type: String, required: true },
  },
  data() {
    return {
      conn: emptyConn(),
      keyInput: '',
      loading: true,
      busy: false,
      error: null,
      showReplace: false,
    };
  },
  computed: {
    /**
     * The site's timezone, same accessor CampaignDetail.vue uses. Passing it is not
     * optional: formatDateTimeWitTz() only converts when a timezone is supplied — with
     * one argument it parses "without conversion", i.e. renders in whichever zone the
     * VIEWER happens to be in. For a support person in another state looking at a
     * practice's settings, that is quietly wrong.
     */
    siteTimezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || 'America/New_York';
    },
    validatedAtDisplay() {
      if (!this.conn.validatedAt) return '—';
      try {
        return this.formatDateTimeWitTz(this.conn.validatedAt, this.siteTimezone);
      } catch (e) {
        return this.conn.validatedAt;
      }
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    ...mapActions('txPresenter', [
      'loadCherryConnection',
      'connectCherry',
      'selectCherryPractice',
      'disconnectCherry',
    ]),

    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        this.conn = { ...emptyConn(), ...(await this.loadCherryConnection({ siteID: this.siteId })) };
      } catch (err) {
        // A load failure is not the same as "not connected" — saying so would invite
        // an operator to re-paste a key over a connection that is actually fine.
        this.error = this.readableError(err, "Couldn't check your Cherry connection.");
      } finally {
        this.loading = false;
      }
      this.emitState();
    },

    async onConnect() {
      const key = this.keyInput.trim();
      if (!key || this.busy) return;
      this.busy = true;
      this.error = null;
      try {
        this.conn = { ...emptyConn(), ...(await this.connectCherry({ siteID: this.siteId, businessApiKey: key })) };
        this.keyInput = '';
        this.showReplace = false;
        this.$bvToast.toast(`Connected to ${this.conn.businessName || 'Cherry'}.`, {
          title: 'Cherry connected', variant: 'success', solid: true, autoHideDelay: 3000,
        });
        this.emitState();
      } catch (err) {
        this.error = this.readableError(err, 'Cherry did not accept that key.');
      } finally {
        this.busy = false;
      }
    },

    async onSelectPractice(practiceId) {
      if (!practiceId || this.busy) return;
      this.busy = true;
      this.error = null;
      try {
        this.conn = { ...emptyConn(), ...(await this.selectCherryPractice({ siteID: this.siteId, practiceId })) };
        this.emitState();
      } catch (err) {
        this.error = this.readableError(err, "Couldn't save the practice location.");
      } finally {
        this.busy = false;
      }
    },

    async onDisconnect() {
      if (this.busy) return;
      const ok = await this.$bvModal.msgBoxConfirm(
        'Disconnect Cherry for this practice? Financing options stop offering real Cherry '
        + 'eligibility until you reconnect. Your key in Cherry is not affected.',
        { title: 'Disconnect Cherry', okTitle: 'Disconnect', okVariant: 'danger', cancelTitle: 'Keep connected' },
      );
      if (!ok) return;

      this.busy = true;
      this.error = null;
      try {
        await this.disconnectCherry({ siteID: this.siteId });
        this.conn = emptyConn();
        this.keyInput = '';
        this.showReplace = false;
        this.emitState();
      } catch (err) {
        this.error = this.readableError(err, "Couldn't disconnect.");
      } finally {
        this.busy = false;
      }
    },

    /**
     * The backend already writes operator-facing text (cherry-service messageFor), so
     * prefer the server's message over axios's "Request failed with status code 400",
     * which tells a practice manager nothing actionable.
     */
    readableError(err, fallback) {
      const fromServer = err && err.response && err.response.data
        && (err.response.data.message || err.response.data.data || err.response.data);
      if (typeof fromServer === 'string' && fromServer.trim()) return fromServer;
      if (err && err.message && !/status code/i.test(err.message)) return err.message;
      return fallback;
    },

    /**
     * Lets the parent know whether a Cherry option is selectable yet. Usable means BOTH
     * a validated key and a chosen location — a connection with no location can't
     * attribute a payment, so offering it would just fail later.
     */
    emitState() {
      const state = {
        connected: !!this.conn.connected,
        usable: !!(this.conn.connected && this.conn.practiceId),
        businessName: this.conn.businessName,
        practiceName: this.conn.practiceName,
      };
      // The plan catalog (what Cherry offers this practice, from get-connection - no Cherry call)
      // rides up with the rest of the state so Settings can draw its plans table from it.
      //   disconnected              -> null: the catalog is deleted with the connection
      //   response carries `catalog` -> that value (an object, or null = never loaded)
      //   response has no `catalog`  -> the key is OMITTED, so the parent keeps whatever it has
      //                                 (e.g. one it just refreshed itself) instead of blanking it
      if (!this.conn.connected) state.catalog = null;
      else if (this.conn.catalog !== undefined) state.catalog = this.conn.catalog || null;
      this.$emit('change', state);
    },
  },
};
</script>

<style scoped>
/* Deliberately mirrors the .sp-* language of SettingsPaymentOptions.vue rather than
   reusing it: those styles are scoped to that component, so its data-v attribute never
   reaches this markup. Own prefix, same visual result. */
.cc-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.cc-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.cc-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #475569;
}
.cc-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1f3f6;
  color: #64748b;
}
.cc-badge-on { background: #e6f1ef; color: #0d5f54; }

.cc-help {
  margin: 0 0 16px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}
.cc-loading { font-size: 13px; color: #64748b; padding: 6px 0; }

.cc-field { margin-bottom: 14px; }
.cc-field > label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 5px;
}
.cc-input {
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.cc-input:focus { outline: none; border-color: #0d5f54; box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.08); }
select.cc-input {
  /* Native selects clip their own chevron on Windows Chrome at this height — same fix
     the rest of Tx Presenter Settings uses. */
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 14px;
  padding-right: 28px;
  /* An explicit height is REQUIRED. Conversations.vue ships an UNSCOPED `select { height: 20px }`
     that leaks app-wide; without our own height the 7px vertical padding + that 20px box clips
     the selected location name (the value is set, the text is cropped). Vertical padding is
     zeroed so the line box, not the padding, centres the text in the 34px box. */
  height: 34px;
  line-height: 1.4;
  padding-top: 0;
  padding-bottom: 0;
}

.cc-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}
.cc-hint-warn { color: #b45309; }
.cc-error {
  margin: 0 0 12px;
  font-size: 12.5px;
  color: #b42318;
  line-height: 1.5;
}

.cc-summary { margin: 0 0 16px; }
.cc-summary-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f6;
  font-size: 13px;
}
.cc-summary-row:last-child { border-bottom: 0; }
.cc-summary-row dt { color: #64748b; font-weight: 500; margin: 0; }
.cc-summary-row dd { color: #0f172a; margin: 0; text-align: right; }
.cc-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

.cc-actions { display: flex; gap: 8px; }
.cc-btn,
.cc-btn-primary {
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
}
.cc-btn:hover:not(:disabled) { background: #f1f3f6; }
.cc-btn-danger { color: #b42318; border-color: #f3d3cf; }
.cc-btn-danger:hover:not(:disabled) { background: #fdf2f0; }
.cc-btn-primary { background: #0d5f54; border-color: #0d5f54; color: #fff; }
.cc-btn-primary:hover:not(:disabled) { background: #094840; }
.cc-btn:disabled,
.cc-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

.cc-replace {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1f3f6;
}
</style>
